import React from "react";
import { Icon, confirmDialog, SampleJobPreview, productRoom, limitPrompt } from "./registry";
import { SAMPLE_DEFAULTS, sampleJob } from "./lib/samplejob";
import { ratesWithDefaults, fmtDur, STEP_RATE_KEYS, STEP_RATE_LABELS, flatStepMinutes, PER_FT_STEP_OF } from "./lib/steptime";
import { buildStepsFor } from "./lib/buildsteps";
import { cleanDesc, DESC_MAX } from "./lib/quotedetails";   // a product's one-line quote description
/* ============================================================
   Fence Works — Product Editor (category-driven)
   Cost-sheet model: components roll up to a panel cost, which the
   engine turns into a per-foot customer price via Settings markup.
   Every dropdown (style, color, post type, cap) and the heights
   matrix is driven by the selected Material Category.
   ============================================================ */
const PE_money = window.VFP.money;
/* feet -> the way a contractor says it ("4'6\"", "42\"") */
const PE_ftIn = (v: any) => {
  const n = Math.round((+v || 0) * 12), ft = Math.floor(n / 12), i = n % 12;
  return i ? (ft ? `${ft}'${i}"` : `${i}"`) : `${ft}'`;
};

/* component TYPE options (full list, per screenshots) */
const COMPONENT_TYPES = [
  "Post", "Rail", "Picket/Board", "Cap", "Bracket", "Fasteners", "Concrete",
  "Stain/Seal", "Panel/Insert", "Mesh/Fabric", "Tension Wire", "Fittings",
  "Insert", "Labor", "Other", "Stringer", "Tread", "Riser", "Baluster",
  "Handrail", "Landing", "Center Column", "Base Plate",
];

const CAT_COLOR = { Wood: "#b87d4b", Vinyl: "#e9edf2", "Chain Link": "#9aa6b8", "Iron/Aluminum": "#2b3340", Composite: "#8a7d6b", "Farm/Ranch": "#7a8a5a", Industrial: "#5a6b7a", Temporary: "#c9a84a" };

/* ---- category configuration ---- */
const CATS = {
  Wood: {
    panelLen: 8, defHeight: 6, heights: [3, 4, 5, 6, 7, 8],
    styles: ["Dog Ear Privacy", "Flat Top Privacy", "Cap & Trim Privacy", "Board on Board", "Board on Board w/ Cap & Trim", "Shadow Box", "Tongue and Groove Privacy", "Horizontal Privacy", "Horizontal w/ Cap & Trim", "Horizontal Semi-Privacy", "Horizontal Slat", "Stockade", "Spaced Picket (Dog Ear)", "Spaced Picket (Flat Top)", "Spaced Picket (Gothic Point)", "Spaced Picket (French Gothic)", "Lattice Top Privacy", "Shadowbox w/ Lattice Top", "Louver / Louvered", "Concave Top Privacy", "Convex Top Privacy", "Post and Rail (2-Rail)", "Post and Rail (3-Rail)", "Split Rail (2-Rail)", "Split Rail (3-Rail)"],
    colors: ["Natural Cedar", "Pressure-Treated Pine", "Redwood", "Cedar w/ Stain", "Spruce / Whitewood", "White (Painted)", "Black (Painted)"],
    postTypes: [{ n: "4×4 Pressure Treated", c: 14.5 }, { n: "4×4 Cedar", c: 18 }, { n: "2-3/8\" Metal Post", c: 22 }, { n: "PostMaster", c: 28 }, { n: "4×6 Pressure Treated", c: 24 }, { n: "4×6 Cedar", c: 30 }, { n: "2-3/8\" Metal Sch40", c: 26 }],
    caps: [{ n: "No Cap", c: 0 }, { n: "Pyramid Cap", c: 3 }, { n: "Ball Cap", c: 5 }, { n: "Solar Cap", c: 18 }],
    steelInsert: false, optionFlag: null,
    template: [
      ["Post", "4×4 Pressure Treated (8')", 1, 14.5, false],
      ["Rail", "2×4×8' PT Rail", 3, 7.25, true],
      ["Picket/Board", "1×6×6' Cedar Dog Ear Picket", 16, 3.5, true],
      ["Bracket", "Rail bracket (Simpson)", 6, 1.75, true],
      ["Fasteners", "#8×2\" deck screws (box)", 1, 4.5, true],
      ["Concrete", "50 lb bag Quikrete", 2, 5.75, false],
      ["Labor", "Install labor per panel", 1, 55, false],
    ],
  },
  Vinyl: {
    panelLen: 6, defHeight: 6, heights: [3, 4, 5, 6, 7, 8],
    styles: ["Solid Privacy", "Privacy w/ Lattice Top", "Privacy w/ Picket Top", "Semi-Privacy (Shadowbox)", "Picket — Straight Top", "Picket — Gothic", "Picket — Scalloped", "Picket — Arched (Convex)", "Horizontal Slat", "Ranch Rail (2-Rail)", "Ranch Rail (3-Rail)", "Ranch Rail (4-Rail)"],
    colors: ["White", "Tan / Almond", "Sand", "Clay / Adobe / Khaki", "Gray", "Charcoal", "Black", "Driftwood (Woodgrain)", "Cypress (Woodgrain)", "Redwood (Woodgrain)", "Mocha Walnut (Woodgrain)", "Two-Tone (specify in notes)"],
    postTypes: [{ n: "5×5 Vinyl Post", c: 42.97 }, { n: "4×4 Vinyl Post", c: 28 }, { n: "5×5 Vinyl + Steel Insert (Gate)", c: 48 }],
    caps: [{ n: "No Cap", c: 0 }, { n: "Flat Cap", c: 4 }, { n: "New England Cap", c: 6 }, { n: "Gothic Cap", c: 8 }, { n: "Solar Lighted Cap", c: 20 }],
    steelInsert: true, optionFlag: null,
    template: [
      ["Post", "5×5 Vinyl Post (8')", 1, 42.97, false],
      ["Rail", "Vinyl Top Rail (routed)", 1, 14, true],
      ["Rail", "Vinyl Bottom Rail (routed)", 1, 16, true],
      ["Picket/Board", "7/8×6\" T&G Privacy Picket", 16, 4.5, true],
      ["Concrete", "50 lb bag Quikrete", 2, 5.75, false],
      ["Fittings", "Misc hardware — glue, screws, rivets (per panel)", 1, 4.5, false],
      ["Labor", "Install labor per panel", 1, 65, false],
    ],
  },
  "Iron/Aluminum": {
    panelLen: 6, defHeight: 5, heights: [3, 3.5, 4, 4.5, 5, 6],
    styles: ["Flat Top (2-Rail)", "Flat Top (3-Rail)", "Spear Top (2-Rail)", "Spear Top (3-Rail)", "Alternating Spear", "Puppy Picket — Flat Top", "Puppy Picket — Spear Top", "Ring Top", "Finial Top", "Majestic (Flush Rail)", "Genesis (Extended Flat Top)", "Invincible (Security Spear)", "Horizontal Slat", "Double Picket"],
    colors: ["Black", "Bronze", "White", "Green"],
    postTypes: [{ n: "2×2 Aluminum Post", c: 22 }, { n: "3×3 Aluminum Post", c: 34 }, { n: "4×4 Aluminum Post", c: 46 }, { n: "5×5 Aluminum Post", c: 62 }, { n: "6×6 Aluminum Post", c: 88 }],
    caps: [{ n: "Standard Cap (incl.)", c: 0 }, { n: "Ball Cap", c: 4 }, { n: "Flat Cap", c: 3 }],
    steelInsert: false, optionFlag: "Custom Powder Coating Available",
    template: [
      ["Post", "2×2 Aluminum Post (7')", 1, 22, false],
      ["Panel/Insert", "Pre-welded panel section", 1, 74, true],
      ["Bracket", "Wall/post bracket kit", 2, 3.5, true],
      ["Fasteners", "Tek screws (pack)", 1, 3, true],
      ["Concrete", "50 lb bag Quikrete", 2, 5, false],
      ["Labor", "Install labor per panel", 1, 55, false],
    ],
  },
  "Chain Link": {
    panelLen: 10, defHeight: 6, heights: [3, 3.5, 4, 5, 6, 7, 8, 10, 12],
    styles: ["Standard Chain Link", "Chain Link w/ Privacy Slats", "Chain Link w/ Windscreen", "Mini Mesh (High Security)", "Commercial / Industrial", "Color Coated (Spectra)", "Pool Code Chain Link", "Temporary / Construction", "Barbed Wire Top Extension"],
    colors: ["Galvanized (Silver)", "Black Vinyl Coated", "Green Vinyl Coated", "Brown Vinyl Coated", "White Vinyl Coated", "Beige Vinyl Coated", "Gray Vinyl Coated"],
    postTypes: [{ n: "1-7/8\" Line Post (Galv)", c: 16 }, { n: "2-3/8\" Terminal Post (Galv)", c: 24 }, { n: "1-7/8\" Line Post (Vinyl Coat)", c: 20 }, { n: "2-3/8\" Terminal Post (Vinyl Coat)", c: 30 }, { n: "3\" OD Gate Post (Galv)", c: 38 }, { n: "3\" OD Gate Post (Vinyl Coat)", c: 46 }, { n: "4\" OD Industrial Post (Galv)", c: 58 }, { n: "6-5/8\" OD Slide Gate Post", c: 120 }],
    caps: [{ n: "Loop Cap", c: 1.5 }, { n: "Dome Cap", c: 2 }, { n: "No Cap", c: 0 }],
    steelInsert: false, optionFlag: null,
    template: [
      ["Post", "1-7/8\" Line Post (Galv) (8')", 1, 16, false],
      ["Mesh/Fabric", "Chain Link Fabric (per section)", 1, 25, true],
      ["Rail", "Top Rail 1-3/8\"", 1, 12, true],
      ["Tension Wire", "Bottom tension wire (per section)", 1, 3, true],
      ["Fittings", "Tension band / brace kit", 1, 6, true],
      ["Concrete", "50 lb bag Quikrete", 2, 5, false],
      ["Labor", "Install labor per section", 1, 45, false],
    ],
  },
  Composite: {
    panelLen: 6, defHeight: 6, heights: [4, 5, 6, 7, 8],
    styles: ["Privacy (Tongue-and-Groove)", "Privacy (Board-on-Board)", "Horizontal Privacy", "Semi-Privacy", "Picket", "Simulated Stone Panel", "Lattice Top Privacy"],
    colors: ["Saddle (Tan/Brown)", "Woodland Brown", "Winchester Grey", "Driftwood (Gray)", "Black", "Coastal Gray", "Warm Sienna", "Cathedral Stone", "Espresso"],
    postTypes: [{ n: "5×5 Composite + Aluminum Insert", c: 60 }, { n: "5×5 Composite + Steel Insert", c: 64 }, { n: "5×5 Composite + 4×4 PT Insert", c: 52 }, { n: "4×4 Composite + Aluminum Insert", c: 48 }, { n: "5×5 Molded Post (SimTek-style)", c: 70 }],
    caps: [{ n: "No Cap", c: 0 }, { n: "Flat Cap", c: 6 }, { n: "Pyramid Cap", c: 8 }],
    steelInsert: true, optionFlag: null,
    template: [
      ["Post", "5×5 Composite + Aluminum Insert (8')", 1, 60, false],
      ["Rail", "Composite top & bottom rail", 2, 12, true],
      ["Picket/Board", "T&G Composite Board", 12, 5.25, true],
      ["Concrete", "50 lb bag Quikrete", 2, 5.75, false],
      ["Labor", "Install labor per panel", 1, 70, false],
    ],
  },
  "Farm/Ranch": {
    panelLen: 16, defHeight: 4, heights: [3, 4, 5],
    styles: ["Field Fence (Woven Wire)", "Barbed Wire", "High-Tensile", "Welded Wire", "2-Rail Wood", "3-Rail Wood", "Vinyl Ranch Rail"],
    colors: ["Galvanized", "Class 3 Galvanized", "Black Powder", "White Vinyl"],
    postTypes: [{ n: "T-Post (Steel)", c: 6 }, { n: "Wood Line Post", c: 9 }, { n: "Wood Corner Post", c: 22 }, { n: "Pipe Post", c: 18 }],
    caps: [{ n: "No Cap", c: 0 }, { n: "T-Post Cap (safety)", c: 1 }],
    steelInsert: false, optionFlag: null,
    template: [
      ["Post", "T-Post 6'", 1, 6, false],
      ["Mesh/Fabric", "Field fence roll (per ft)", 16, 0.85, true],
      ["Fasteners", "Fence staples / clips", 1, 1.5, true],
      ["Labor", "Install labor per run", 1, 30, false],
    ],
  },
  Industrial: {
    panelLen: 10, defHeight: 8, heights: [6, 7, 8, 10, 12],
    styles: ["Security Mesh (358)", "Palisade", "Anti-Climb", "Welded Wire Panel", "Expanded Metal", "Razor-Top Chain Link"],
    colors: ["Galvanized", "Black Powder", "Green Powder", "Hot-Dip Galv"],
    postTypes: [{ n: "3\" OD Galv Post", c: 48 }, { n: "4\" OD Galv Post", c: 64 }, { n: "HSS Square Post", c: 80 }],
    caps: [{ n: "Welded Cap", c: 0 }, { n: "Dome Cap", c: 3 }],
    steelInsert: false, optionFlag: "Anti-Climb / Anti-Cut Rated",
    template: [
      ["Post", "3\" OD Galv Post (10')", 1, 48, false],
      ["Panel/Insert", "358 Mesh Panel", 1, 145, true],
      ["Fittings", "Security clamp bar set", 2, 8, true],
      ["Concrete", "60 lb bag", 3, 6, false],
      ["Labor", "Install labor per panel", 1, 90, false],
    ],
  },
  Temporary: {
    panelLen: 12, defHeight: 6, heights: [6],
    styles: ["Panel Fence (Free-Standing)", "Construction Chain Link", "Crowd Control Barrier"],
    colors: ["Galvanized", "Orange Safety"],
    postTypes: [{ n: "Panel Foot (rubber)", c: 12 }, { n: "Drive Post", c: 8 }],
    caps: [{ n: "No Cap", c: 0 }],
    steelInsert: false, optionFlag: null,
    template: [
      ["Panel/Insert", "6'×12' Temp Panel", 1, 62, true],
      ["Fittings", "Panel clamp", 2, 2.5, true],
      ["Labor", "Set / strike per panel", 1, 14, false],
    ],
  },
};
const FENCE_CATEGORIES = Object.keys(CATS);

function blankFor(category) {
  const c = CATS[category] || CATS.Wood;
  const post = c.postTypes[0];
  return {
    category, style: c.styles[0], customName: "", color: c.colors[0],
    height: c.defHeight, panelLength: c.panelLen, notes: "",
    postType: post.n, postCost: post.c,
    cap: c.caps[0].n, capCost: c.caps[0].c,
    steelInsertCost: 18, steelInsertQty: 0, optionFlagOn: false,
    flatRate: false, flatPerFt: 17,
    // How the posts go in the ground, and how deep. This drives the 3D build's
    // post/collar geometry and every depth it prints, so a driven-post crew
    // stops seeing a concrete-set picture (and a 24" callout) on their own product.
    postSet: category === "Vinyl" ? "driven" : "concrete",
    embedIn: category === "Vinyl" ? 42 : 24,
    pipeFt: 8,
    heightPricing: {},   // { '8': perFtNumber }
    components: c.template.map(([type, desc, qty, unit, scales]) => ({ type, desc, qty, unit, scales, locked: type === "Post" })),
    gates: { walk: "", drive10: "", drive12: "" },
    uchannelCost: 4, uchannelLaborMin: 1.5,
  };
}

/* ---- how the posts go in, and how deep --------------------------------
   The 3D build used to model one fixed depth per material and print a flat
   24" callout next to it — so a crew that drives 42" steel was shown a
   picture and a number that were not their fence. Both come from here now:
   the method decides what's drawn, the depth decides how deep, and on a
   driven post the pipe length decides how much steel stands above grade for
   the sleeve to lock onto. */
const POST_SET_OPTS = [
  { id: "driven", label: "Driven", sub: "Steel pipe driven into undisturbed soil — no concrete" },
  { id: "concrete", label: "Concrete-set", sub: "Post set in a poured footing below the frost line" },
];
function PostSetting({ f, set }: any) {
  const driven = f.postSet === "driven" || (f.postSet !== "concrete" && f.category === "Vinyl");
  const embedIn = Math.max(6, Math.min(84, +f.embedIn || (driven ? 42 : 24)));
  const pipeFt = Math.max(3, Math.min(14, +f.pipeFt || 8));
  const exposedFt = pipeFt - embedIn / 12;
  const tooDeep = driven && exposedFt < 1;      // more pipe in the ground than the fence is tall
  return (
    <div className="post-set">
      <div style={{ fontWeight: 700, fontSize: 14.5 }}>How do these posts go in?</div>
      <div className="muted" style={{ fontSize: 12, margin: "3px 0 10px", maxWidth: 580 }}>
        Sets what the 3D build shows your customer and every depth it prints — the post, the collars, and the embed callout.
      </div>
      <div className="post-set-opts" role="group" aria-label="Post setting method">
        {POST_SET_OPTS.map(o => (
          <button key={o.id} type="button" className={"post-set-opt" + ((o.id === "driven") === driven ? " on" : "")}
            aria-pressed={(o.id === "driven") === driven}
            onClick={() => set({ postSet: o.id, embedIn: o.id === "driven" ? 42 : 24 })}>
            <span className="post-set-opt-label">{o.label}</span>
            <span className="post-set-opt-sub">{o.sub}</span>
          </button>
        ))}
      </div>
      <div className="pe-row2" style={{ marginTop: 12 }}>
        <Field label={driven ? "Drive depth (in)" : "Footing depth (in)"} hint={driven ? "how far the pipe goes below grade" : "depth of the pour below grade"}>
          <input className="input tnum" type="number" inputMode="numeric" min="6" max="84" value={f.embedIn ?? embedIn}
            onChange={e => set({ embedIn: e.target.value === "" ? "" : Math.max(6, Math.min(84, +e.target.value || 0)) })} />
        </Field>
        {driven && (
          <Field label="Steel pipe length (ft)" hint="depth + what stands above grade">
            <input className="input tnum" type="number" inputMode="numeric" min="3" max="14" value={f.pipeFt ?? pipeFt}
              onChange={e => set({ pipeFt: e.target.value === "" ? "" : Math.max(3, Math.min(14, +e.target.value || 0)) })} />
          </Field>
        )}
      </div>
      {driven && (
        <p className={tooDeep ? "post-set-warn" : "faint"} style={{ fontSize: 12, margin: "8px 0 0" }}>
          {tooDeep
            ? <>{/^[8]/.test(String(pipeFt)) ? "An" : "A"} {pipeFt}' pipe driven {Math.round(embedIn)}" leaves almost nothing above grade — the sleeve has no steel to lock onto. Use a longer pipe or a shallower drive.</>
            : <>{/^[8]/.test(String(pipeFt)) ? "An" : "A"} {pipeFt}' pipe driven {Math.round(embedIn)}" leaves <b style={{ color: "var(--teal)" }}>{PE_ftIn(exposedFt)}</b> of steel above grade — that's what the sleeve slides over and the top collar clamps to.</>}
        </p>
      )}
    </div>
  );
}

function ProductEditor({ store, go, params }: any) {
  // duplicating opens a NEW product seeded from an existing one, so the source
  // is never at risk — the review asked for Duplicate beside Edit
  const dupeFrom = params && params.duplicate ? store.allProducts.find((p: any) => p.id === params.duplicate) : null;
  const editing = params && params.id ? (store.allProducts.find(p => p.id === params.id)) : null;
  // plan cap: a NEW product past it can't be saved (the store refuses too);
  // editing one that exists always saves, over the cap or not
  const room = productRoom(store);
  const capped = !editing && room.atLimit;
  // a "new product in this category" arrives from the coverage rail's Add
  const seed = editing && editing._editor ? editing._editor
    : editing ? editorFromProduct(editing)
    : dupeFrom ? (() => { const d = JSON.parse(JSON.stringify(dupeFrom._editor || editorFromProduct(dupeFrom))); d.customName = ((dupeFrom.name || "Product") + " copy"); return d; })()
    : blankFor((params && params.category && CATS[params.category]) ? params.category : "Wood");
  const [f, setF] = React.useState<any>(() => {
    // normalize against older/partial saved editor blobs so the render never hits
    // an undefined field (e.g. customName.trim() crashing on a legacy product).
    const s = JSON.parse(JSON.stringify(seed));
    if (typeof s.customName !== "string") s.customName = "";
    // the one line under this fence on every quote (lib/quotedetails.ts) — kept
    // on the product itself too, so a product saved without the editor has it
    if (typeof s.description !== "string") s.description = (editing && typeof editing.description === "string") ? editing.description : "";
    if (!Array.isArray(s.components)) s.components = [];
    // products saved before posts were configurable: fall back to the method
    // this material is actually built with, never to whatever `undefined` reads as
    if (s.postSet !== "driven" && s.postSet !== "concrete") s.postSet = s.category === "Vinyl" ? "driven" : "concrete";
    if (!isFinite(+s.embedIn) || +s.embedIn <= 0) s.embedIn = s.postSet === "driven" ? 42 : 24;
    if (!isFinite(+s.pipeFt) || +s.pipeFt <= 0) s.pipeFt = 8;
    if (!s.gates || typeof s.gates !== "object") s.gates = { walk: "", drive10: "", drive12: "" };
    // gate OPTIONS list (one line per gate you offer; ◉ default per size tier
    // prices the quote) — seeded from the legacy flat tier boxes on first open
    if (!Array.isArray(s.gates.list)) {
      const seed = [];
      [["walk", "Walk gate"], ["drive10", "Drive gate — 10 ft"], ["drive12", "Drive gate — 12 ft"]].forEach(([k, nm]) => {
        const n = parseFloat(s.gates[k]); if (isFinite(n) && n > 0) seed.push({ id: "g-" + k, name: nm, tier: k, cost: n, sku: "", supplier: "", default: true });
      });
      s.gates.list = seed;
    }
    if (!s.heightPricing || typeof s.heightPricing !== "object") s.heightPricing = {};
    if (s.uchannelCost == null) s.uchannelCost = 4;
    if (s.uchannelLaborMin == null) s.uchannelLaborMin = 1.5;
    // sparse per-product install-time overrides (only changed step keys); absent = inherit global/default.
    // fall back to the product's top-level stepRates if the editor blob predates this field.
    if (!s.stepRates || typeof s.stepRates !== "object") s.stepRates = (editing && editing.stepRates && typeof editing.stepRates === "object") ? { ...editing.stepRates } : {};
    return s;
  });
  const set = (patch) => setF(prev => ({ ...prev, ...patch }));
  const [toast, setToast] = React.useState<any>(null);
  function flash(m) { setToast(m); setTimeout(() => setToast(null), 2200); }
  // "Install time per step" override section — collapsed unless this product
  // already overrides something, so it stays out of the way until wanted.
  const [stepTimeOpen, setStepTimeOpen] = React.useState<any>(() => {
    const sr = (seed && seed.stepRates) || (editing && editing.stepRates);
    return !!(sr && typeof sr === "object" && Object.keys(sr).length > 0);
  });

  const cat = CATS[f.category] || CATS.Wood;
  const baseLen = cat.panelLen;

  // derived
  const panelTotal = f.components.reduce((t, c) => t + (+c.qty || 0) * (+c.unit || 0), 0);
  const perFoot = f.flatRate ? (+f.flatPerFt || 0) : (f.panelLength > 0 ? panelTotal / f.panelLength : 0);
  const per100 = perFoot * 100;
  const displayName = f.customName.trim() || `${f.height} ft ${f.style}`;

  // labor $/ft for this category (from Settings crews)
  const S = Object.assign({}, window.VFP.DEFAULT_SETTINGS, store.settings);
  const crew = (S.crews || []).find(c => c.id === S.activeCrewId) || (S.crews || [])[0] || { size: 2, wage: 28 };
  const crewRate = (+crew.size || 2) * (+crew.wage || 28);
  const hrs300 = (S.laborHours && S.laborHours[f.category] != null) ? S.laborHours[f.category] : S.laborHoursDefault;
  const laborPerFt = (hrs300 * crewRate) / 300;
  const marginPct = S.markup > 0 ? (1 - 1 / S.markup) * 100 : 0;

  /* What the preview prices. Untouched, it prices the SAVED product, so opening
     a product shows the same number the catalog shows — the point of PROD-03.
     Once the form is edited it prices the edited form, and says so, because
     that is the number saving would produce. (The seeded catalog products carry
     a component shape this editor doesn't round-trip, so the two genuinely
     differ once you save; showing that is better than hiding it.) */
  // snapshot the NORMALISED initial form (the state initialiser fills in
  // defaults, so comparing against the raw seed would read as edited at once)
  const seedSnapshot = React.useRef<string | null>(null);
  if (seedSnapshot.current === null) seedSnapshot.current = JSON.stringify(f);
  const edited = JSON.stringify(f) !== seedSnapshot.current;
  const [sampleInputs, setSampleInputs] = React.useState<any>(SAMPLE_DEFAULTS);
  const formProduct = React.useMemo(() => {
    const post = f.components.find((c: any) => c.type === "Post");
    const concrete = f.components.find((c: any) => c.type === "Concrete");
    const panelCostExcl = f.components.filter((c: any) => c.type !== "Post" && c.type !== "Concrete").reduce((t: number, c: any) => t + (+c.qty || 0) * (+c.unit || 0), 0) + (+f.capCost || 0);
    const gate = (f.gates.list || []).find((g: any) => g.tier === "walk" && g.default) || (f.gates.list || [])[0];
    return {
      id: editing ? editing.id : "preview", name: displayName, category: f.category,
      panelWidth: +f.panelLength || baseLen,
      panelCost: f.flatRate ? (+f.flatPerFt || 0) * (+f.panelLength || baseLen) : panelCostExcl,
      postKit: post ? (+post.unit || 0) + (+f.capCost || 0) : (+f.postCost || 0),
      sleeveCost: 0, centering: 0, adhesiveCost: 0, adhesivePerPosts: 999,
      concretePerBag: concrete ? (+concrete.unit || 0) : 0,
      bagsPerPost: concrete ? (+concrete.qty || 0) : 0,
      gateCost: gate ? (+gate.cost || 0) : 165, allSteel: false, custom: true,
    };
  }, [f, baseLen, displayName, editing]);
  const previewProduct = (editing && !edited) ? editing : formProduct;
  const stickyJob = sampleJob(previewProduct, store.settings, sampleInputs);

  // supplier names for the per-component supplier datalist (inventory suppliers)
  const [supNames, setSupNames] = React.useState<any>([]);
  const [optOpen, setOptOpen] = React.useState<any>(null);   // component index with its options strip expanded
  React.useEffect(() => {
    const ST: any = (window as any).VFP_STOCK;
    if (ST && ST.enabled()) ST.suppliers().then((r: any) => setSupNames((r || []).map((s: any) => s.name).filter(Boolean))).catch(() => {});
  }, []);

  /* PROD-04: switching category REPLACES the whole form with that category's
     template — different styles, colours, posts, caps and a different component
     list. It used to happen on the select's change event with no warning, so a
     misclick wiped a filled-in cost sheet. */
  async function changeCategory(category) {
    if (edited || f.components.some((c: any) => (+c.unit || 0) > 0)) {
      const ok = await confirmDialog({
        title: `Switch to ${category}?`,
        body: `${category} fences use different posts, caps, styles and components, so this replaces the whole cost sheet with the ${category} template. Your ${f.components.length} component rows, gate options and any per-height pricing on this form go with it.`,
        confirmLabel: `Switch to ${category}`, cancelLabel: "Keep what I have", danger: true,
      });
      if (!ok) return;
    }
    setF(JSON.parse(JSON.stringify(blankFor(category))));
    seedSnapshot.current = null;   // the template is the new baseline
  }
  /* "Catalog Coverage → Add" called changeCategory() while the editor kept the
     product it was editing — so pressing Add on a category quietly turned the
     open product into a different one. It starts a NEW product now. */
  async function addCategory(category) {
    if (editing) {
      const ok = await confirmDialog({
        title: `Start a new ${category} product?`,
        body: edited
          ? `You have unsaved changes to "${editing.name}". Starting a new product discards them.`
          : `This leaves "${editing.name}" as it is and opens a blank ${category} product.`,
        confirmLabel: `New ${category} product`, cancelLabel: "Stay here", danger: edited,
      });
      if (!ok) return;
      go("product-edit", { new: true, category });
      return;
    }
    changeCategory(category);
  }
  function updateRow(i, patch) { setF(prev => ({ ...prev, components: prev.components.map((c, k) => k === i ? { ...c, ...patch } : c) })); }
  // per-row OPTIONS (colors / supplier alternates): "Add option" duplicates the
  // live row's values; "Use" swaps an option back in as the live pricing row.
  function addOption(i) { setF(prev => ({ ...prev, components: prev.components.map((c, k) => k === i ? { ...c, options: [...(c.options || []), { id: "o" + Date.now() + "-" + Math.floor(Math.random() * 99), desc: c.desc, sku: c.sku || "", supplier: c.supplier || "", unit: c.unit }] } : c) })); }
  function setOption(i, oi, patch) { setF(prev => ({ ...prev, components: prev.components.map((c, k) => k === i ? { ...c, options: (c.options || []).map((o, j) => j === oi ? { ...o, ...patch } : o) } : c) })); }
  function useOption(i, oi) { setF(prev => ({ ...prev, components: prev.components.map((c, k) => { if (k !== i) return c; const o = (c.options || [])[oi]; return o ? { ...c, desc: o.desc || "", sku: o.sku || "", supplier: o.supplier || "", unit: +o.unit || 0 } : c; }) })); }
  function delOption(i, oi) { setF(prev => ({ ...prev, components: prev.components.map((c, k) => k === i ? { ...c, options: (c.options || []).filter((_, j) => j !== oi) } : c) })); }
  function addRow() { setF(prev => ({ ...prev, components: [...prev.components, { type: "Other", desc: "", qty: 1, unit: 0, scales: true }] })); }
  function removeRow(i) { setF(prev => ({ ...prev, components: prev.components.filter((_, k) => k !== i) })); }
  async function resetTemplate() {
    const ok = await confirmDialog({
      title: "Reset this product to the template?",
      body: `This throws away your ${f.components.length} component rows and their costs, the post and cap settings, gate options and any per-height pricing, and puts the stock ${f.category} template back. The name, style and colour are kept.`,
      confirmLabel: "Reset to template", cancelLabel: "Keep my costs", danger: true,
    });
    if (!ok) return;
    setF(prev => ({ ...JSON.parse(JSON.stringify(blankFor(prev.category))), customName: prev.customName, style: prev.style, color: prev.color }));
    flash("Reset to template");
  }
  function setPostType(n) { const pt = cat.postTypes.find(p => p.n === n) || cat.postTypes[0]; setF(prev => ({ ...prev, postType: n, postCost: pt.c, components: prev.components.map(c => c.type === "Post" ? { ...c, unit: pt.c } : c) })); }
  function setCap(n) { const cp = cat.caps.find(c => c.n === n) || cat.caps[0]; set({ cap: n, capCost: cp.c }); }
  function toggleHeight(h) { setF(prev => { const hp = { ...prev.heightPricing }; if (hp[h] != null) delete hp[h]; else hp[h] = ""; return { ...prev, heightPricing: hp }; }); }
  function applyAllHeights() { const v = perFoot ? perFoot.toFixed(2) : ""; setF(prev => { const hp = {}; cat.heights.forEach(h => hp[h] = v); return { ...prev, heightPricing: hp }; }); flash("Applied current $/ft to all heights"); }

  // ---- install-time per-step overrides (sparse on f.stepRates; blank = inherit) ----
  // effective global/default rates, used to pre-fill each input's placeholder so
  // the contractor sees what they're overriding (global Settings stepRates, else seed).
  const globalRates = ratesWithDefaults(S.stepRates);
  // set/clear one override key: "" removes it (→ inherit global); a number stores it
  function setStepRate(key, val) {
    setF(prev => {
      const sr = { ...(prev.stepRates || {}) };
      if (val === "" || val == null) delete sr[key];
      else sr[key] = Math.max(0, +val || 0);
      return { ...prev, stepRates: sr };
    });
  }
  function clearStepRate(key) { setStepRate(key, ""); }
  function resetStepRates() { setF(prev => ({ ...prev, stepRates: {} })); flash("Step times reset to global"); }
  // the step keys worth showing for THIS fence type: per-unit classes that its
  // build steps actually price from, plus the two flat survey steps (+ their
  // optional per-100ft companions). Falls back to all keys if nothing resolves.
  const relevantStepKeys = React.useMemo(() => stepKeysForCategory(f.category), [f.category]);
  const perFtKeySet = new Set(Object.values(PER_FT_STEP_OF));   // "Mark property/ft", "Shoot grade/ft"
  const hasPerFt = relevantStepKeys.some(k => perFtKeySet.has(k));
  const overrideCount = Object.keys(sparseStepRates(f.stepRates)).length;

  /* Leaving with unsaved costs on the form used to lose them without a word —
     Cancel, the browser back button, or a nav click. */
  async function leave() {
    if (edited) {
      const ok = await confirmDialog({
        title: "Leave without saving?",
        body: `Your changes to ${displayName || "this product"} haven't been saved. They'll be gone.`,
        confirmLabel: "Discard changes", cancelLabel: "Keep editing", danger: true,
      });
      if (!ok) return;
    }
    go("products");
  }
  React.useEffect(() => {
    if (!edited) return;
    const warn = (e: any) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [edited]);

  function save() {
    if (capped) { limitPrompt(store, go, "products"); return; }
    if (!f.style) { flash("Pick a style first"); return; }
    const post = f.components.find(c => c.type === "Post");
    const concrete = f.components.find(c => c.type === "Concrete");
    const panelCostExcl = f.components.filter(c => c.type !== "Post" && c.type !== "Concrete").reduce((t, c) => t + (+c.qty || 0) * (+c.unit || 0), 0)
      + (+f.capCost || 0) + (f.steelInsertCost && f.steelInsertQty ? 0 : 0);
    const product: any = {
      id: editing ? editing.id : "cp" + Date.now(),
      name: displayName, sub: `${f.color} · ${f.style}`, category: f.category,
      color: CAT_COLOR[f.category] || "#9aa6b8",
      panelWidth: +f.panelLength || baseLen,
      panelCost: f.flatRate ? (+f.flatPerFt || 0) * (+f.panelLength || baseLen) : panelCostExcl,
      postKit: post ? (+post.unit || 0) + (+f.capCost || 0) : (+f.postCost || 0),
      sleeveCost: 0, centering: 0, adhesiveCost: 0, adhesivePerPosts: 999,
      concretePerBag: concrete ? (+concrete.unit || 0) : 0,
      bagsPerPost: concrete ? (+concrete.qty || 0) : 0,
      gateCost: parseFloat(f.gates.walk) || 165, allSteel: false, custom: true, _editor: f,
    };
    // the price book's default line on a quote; each quote can change it
    const description = cleanDesc(f.description);
    if (description) product.description = description;
    // surface the sparse install-time overrides at the top level so the crew
    // sheet / scheduler can resolve them without unpacking _editor. Only attach
    // when the contractor actually overrode something — keeps products lean.
    const sr = sparseStepRates(f.stepRates);
    if (Object.keys(sr).length) product.stepRates = sr;
    if (!store.saveProduct(product, !editing)) return;   // refused at the plan cap (the store says why)
    seedSnapshot.current = JSON.stringify(f);   // saved: this is the new baseline
    flash(editing ? "Saved" : "Product created");
    setTimeout(() => go("products"), 450);
  }
  // Every other delete in the app confirms first (the estimate row, and the work
  // order's type-the-name dialog). This one went straight through on one tap.
  async function del() {
    if (!editing) return;
    const used = (store.quotes || []).filter((q: any) => q.productId === editing.id).length;
    const ok = await confirmDialog({
      title: `Delete "${editing.name}"?`,
      body: used > 0
        ? `${used} estimate${used === 1 ? "" : "s"} were priced with this product. They keep the numbers they were quoted at, but you won't be able to rebuild one from this product afterwards.`
        : "This removes it from your price book. Estimates already quoted keep their numbers.",
      confirmLabel: "Delete product", cancelLabel: "Keep it", danger: true,
    });
    if (!ok) return;
    store.deleteProduct(editing.id);
    go("products");
  }

  /* PROD-06: "Catalog Coverage" counted all eight categories and called every
     one the business doesn't sell "missing", so a wood-only installer could
     never be finished. Only what they say they sell counts — and until they
     say, what they already have a product in is the answer. */
  const soldRaw = (store.settings && store.settings.materialsSold);
  const declared = Array.isArray(soldRaw) && soldRaw.length ? soldRaw : null;
  const sold = declared || FENCE_CATEGORIES.filter(c => store.allProducts.some((p: any) => p.category === c));
  const missing = sold.filter(c => !store.allProducts.some(p => p.category === c));
  function setSold(next: string[]) { store.setSettings && store.setSettings({ materialsSold: next }); }
  const benchmarks = [["Wood Privacy", "$25–$40/ft"], ["Vinyl Privacy", "$30–$50/ft"], ["Chain Link", "$15–$25/ft"], ["Ornamental Iron", "$35–$60/ft"], ["Composite", "$30–$55/ft"]];

  return (
    <div className="page fade-up pe-page">
      <div className="pe-head">
        <div>
          <span className="kicker kicker-teal">{editing ? "Edit Product" : "New Product"}</span>
          <h1 className="display" style={{ fontSize: 26, margin: "4px 0 0" }}>{editing ? `Edit: ${editing.name}` : "Add a Product"}</h1>
        </div>
        <div className="row gap-8">
          <button className="btn btn-ghost" onClick={leave}>Cancel</button>
          <button className="btn btn-primary" onClick={save}><Icon name={capped ? "lock" : "download"} size={15} />Save Product</button>
        </div>
      </div>
      {capped && (
        <div className="pe-callout warn" style={{ marginBottom: 14 }}>
          <div className="row between" style={{ gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13.5 }}><b style={{ color: "var(--amber)" }}>{room.over ? `${room.plan.name} holds ${room.limit} products — you have ${room.used}.` : `${room.used} of ${room.limit} products on ${room.plan.name}.`}</b> <span className="muted">Upgrade to add this one — every product you already have stays editable.</span></span>
            <button className="btn btn-primary btn-sm" onClick={() => limitPrompt(store, go, "products")}><Icon name="bolt" size={13} />Upgrade</button>
          </div>
        </div>
      )}

      <div className="pe-grid">
        {/* left rail */}
        <aside className="pe-side">
          <div className="card pad">
            <div className="row between" style={{ marginBottom: 12 }}><span className="kicker">Catalog Stats</span><span className="chip teal" style={{ fontSize: 10, padding: "1px 7px" }}>{store.allProducts.length} products</span></div>
            {[["Total Products", store.allProducts.length], ["Categories", new Set(store.allProducts.map(p => p.category)).size + " of 8"]].map(([k, v]) =>
              <div key={k} className="row between" style={{ padding: "7px 0", borderBottom: "1px solid var(--line)", fontSize: 13 }}>
                <span className="muted">{k}</span><span className="tnum" style={{ fontWeight: 700 }}>{v}</span></div>)}
          </div>
          {/* PROD-03: the real engine, not material-cost × markup. This panel
              used to show ~$20.35/ft for a product the catalog priced at
              $26.19/ft — two different questions answered as if they were one. */}
          <div className="card pad">
            <SampleJobPreview product={previewProduct} settings={store.settings} inputs={sampleInputs} onInputs={setSampleInputs} title={edited ? "Sample job · unsaved edits" : "Sample job price"} />
            <div className="mc-note">{edited
              ? "Priced from your unsaved edits by the same engine that prices a customer's estimate — this is what saving would produce."
              : "Priced by the same engine that prices a customer's estimate, on the same sample job the catalog uses. Change the footage or gates to sanity-check it."}</div>
          </div>
          <div className="card pad">
            <div className="row between" style={{ marginBottom: 10 }}><span className="kicker">Pricing Benchmarks</span><span className="chip amber" style={{ fontSize: 10, padding: "1px 7px" }}>Regional</span></div>
            {benchmarks.map(([k, v]) => <div key={k} className="row between" style={{ padding: "6px 0", fontSize: 12.5 }}><span className="muted">{k}</span><span className="mono" style={{ color: "var(--teal)", fontSize: 12 }}>{v}</span></div>)}
            <div className="faint" style={{ fontSize: 10.5, marginTop: 8 }}>Regional installed pricing (2025 avg)</div>
          </div>
        </aside>

        {/* main */}
        <main className="pe-main">
          <div className="pe-callout info">
            <div className="row gap-8" style={{ fontWeight: 700, color: "var(--teal)", marginBottom: 6 }}><Icon name="bolt" size={15} />This is your cost sheet — what YOU pay for materials</div>
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>Enter what your distributor or supplier charges you — for the fence parts <b style={{ color: "var(--fg-2)" }}>and the gates below</b>. When you build a quote, the app adds labor and your markup on top. Each gate shows as its own marked-up <b style={{ color: "var(--fg-2)" }}>“Gate Installed”</b> line. <b style={{ color: "var(--amber)" }}>Tear-out is a charge, not a cost</b> — that one isn't marked up.</p>
          </div>

          {/* product info */}
          <Block icon="box" title="Product Info">
            <div className="pe-row2">
              <Field label="Material Category">
                <select className="select" value={f.category} onChange={e => changeCategory(e.target.value)}>{FENCE_CATEGORIES.map(c => <option key={c}>{c}</option>)}</select>
              </Field>
              <Field label="Style">
                <select className="select" value={f.style} onChange={e => set({ style: e.target.value })}>{cat.styles.map(s => <option key={s}>{s}</option>)}</select>
              </Field>
            </div>
            <Field label="Custom Display Name" hint="optional — what you call this style in your area">
              <input className="input" placeholder="e.g. Stockade, Shadowbox, Board-on-Board…" value={f.customName} onChange={e => set({ customName: e.target.value })} />
            </Field>
            <Field label="Quote description" hint="optional — one line under this fence on every quote: the grade of material, what's included">
              <input className="input" maxLength={DESC_MAX} data-product-description placeholder="e.g. Western red cedar #1 boards on 4×4 posts set in concrete"
                value={f.description || ""} onChange={e => set({ description: e.target.value.replace(/[\r\n]+/g, " ") })} />
            </Field>
            <div className="pe-row3">
              <Field label="Color / Finish">
                <select className="select" value={f.color} onChange={e => set({ color: e.target.value })}>{cat.colors.map(c => <option key={c}>{c}</option>)}</select>
              </Field>
              <Field label="Default Height (ft)">
                <select className="select" value={f.height} onChange={e => set({ height: +e.target.value })}>{cat.heights.map(h => <option key={h} value={h}>{h} ft</option>)}</select>
              </Field>
              <Field label="Panel Length (ft)" hint="base length; scaled items adjust from here">
                <input className="input tnum" type="number" min="1" value={f.panelLength} onChange={e => set({ panelLength: +e.target.value || 0 })} />
              </Field>
            </div>

            <PostSetting f={f} set={set} />

            {/* heights matrix */}
            <div className="height-box">
              <div className="row between" style={{ flexWrap: "wrap", gap: 8 }}>
                <div><div style={{ fontWeight: 700, fontSize: 14.5 }}>Does this fence cost you different at different heights?</div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 3, maxWidth: 560 }}>Most don't — you cut the same boards shorter for 4ft vs 6ft, same cost per foot. <b style={{ color: "var(--fg-2)" }}>Only check a height</b> if your supplier charges more or less for that size.</div></div>
                <button className="btn btn-ghost btn-sm" onClick={applyAllHeights}><Icon name="layers" size={13} />Apply to All</button>
              </div>
              <div className="height-grid">
                {cat.heights.map(h => {
                  const on = f.heightPricing[h] != null;
                  return (
                    <div key={h} className={"height-row" + (on ? " on" : "")}>
                      <button className={"cbox" + (on ? " on" : "")} onClick={() => toggleHeight(h)}>{on && <Icon name="check" size={11} />}</button>
                      <span style={{ fontWeight: 700, fontSize: 13.5 }}>{h}ft</span>
                      {on
                        ? <div className="pe-money-input" style={{ marginLeft: "auto", maxWidth: 130 }}><span>$</span><input className="tnum" type="number" placeholder="/ft" value={f.heightPricing[h]} onChange={e => setF(prev => ({ ...prev, heightPricing: { ...prev.heightPricing, [h]: e.target.value } }))} /></div>
                        : <span className="faint" style={{ marginLeft: "auto", fontSize: 12, fontStyle: "italic" }}>Same as {perFoot ? PE_money(perFoot) : "—"}/ft</span>}
                    </div>
                  );
                })}
              </div>
            </div>

            <Field label="Notes"><input className="input" placeholder="e.g. 9ga, 2&quot; mesh, custom specs" value={f.notes} onChange={e => set({ notes: e.target.value })} /></Field>
          </Block>

          {/* post config */}
          <Block icon="ruler" title="Post Configuration">
            <div className="pe-row4">
              <Field label="Post Type">
                <select className="select" value={f.postType} onChange={e => setPostType(e.target.value)}>{cat.postTypes.map(p => <option key={p.n}>{p.n}</option>)}</select>
              </Field>
              <Field label="Post Length (auto)" hint={`fence ${f.height}' + 2' in ground`}>
                <input className="input tnum" readOnly value={`${f.height + 2}'`} style={{ color: "var(--fg-3)", textAlign: "center" }} />
              </Field>
              <Field label="Cost Per Post ($)">
                <input className="input tnum" type="number" value={f.postCost} onChange={e => setF(prev => ({ ...prev, postCost: +e.target.value || 0, components: prev.components.map(c => c.type === "Post" ? { ...c, unit: +e.target.value || 0 } : c) }))} />
              </Field>
              <Field label="Post $/ea">
                <div className="post-readout">{PE_money(+f.postCost || 0)}</div>
              </Field>
            </div>
            {cat.steelInsert &&
              <div className="pe-row2" style={{ marginTop: 12 }}>
                <Field label="Steel Insert — Cost Each"><input className="input tnum" type="number" value={f.steelInsertCost} onChange={e => set({ steelInsertCost: +e.target.value || 0 })} /></Field>
                <Field label="Steel Inserts — Qty Per Job" hint="gate posts only — set per bid on main page"><input className="input tnum" type="number" value={f.steelInsertQty} onChange={e => set({ steelInsertQty: +e.target.value || 0 })} /></Field>
              </div>}
            <div className="pe-row2" style={{ marginTop: 12 }}>
              <Field label="Post Cap Style">
                <select className="select" value={f.cap} onChange={e => setCap(e.target.value)}>{cat.caps.map(c => <option key={c.n}>{c.n}{c.c ? ` — ${PE_money(c.c)}` : ""}</option>)}</select>
              </Field>
              <Field label="Cap Cost Each"><input className="input tnum" type="number" value={f.capCost} onChange={e => set({ capCost: +e.target.value || 0 })} /></Field>
            </div>
            {cat.optionFlag &&
              <button className={"opt-flag" + (f.optionFlagOn ? " on" : "")} onClick={() => set({ optionFlagOn: !f.optionFlagOn })} style={{ marginTop: 12 }}>
                <span className={"cbox" + (f.optionFlagOn ? " on" : "")}>{f.optionFlagOn && <Icon name="check" size={11} />}</span>
                <span style={{ fontWeight: 600, fontSize: 13.5 }}>{cat.optionFlag}</span>
                <span className="chip" style={{ fontSize: 9.5, marginLeft: 8 }}>OPTION FLAG</span>
                <span className="faint" style={{ fontSize: 11.5, marginLeft: "auto" }}>Shows on quote — pricing handled separately</span>
              </button>}
          </Block>

          {/* PROD-02: two cost methods, said out loud. The flat rate was a
              toggle buried under a red "RECOMMENDED" chip while the component
              table sat below it regardless, so it was never clear which one was
              actually pricing the product, or what either included. */}
          <Block icon="dollar" title="Cost method">
            <p className="muted" style={{ fontSize: 12.5, margin: "0 0 12px" }}>How do you want to tell the app what this fence costs you? Both feed the same engine — pick whichever you actually know.</p>
            <div className="cost-methods">
              <button className={"cost-method" + (f.flatRate ? " on" : "")} aria-pressed={!!f.flatRate} onClick={() => set({ flatRate: true })}>
                <span className="cost-method-h"><Icon name="bolt" size={14} />Material cost per foot<span className="chip teal" style={{ fontSize: 9, padding: "1px 6px" }}>FASTEST</span></span>
                <span className="faint">One number: what the materials cost you per foot of fence. <b>Includes posts and concrete.</b> Labor and markup are added on top from Settings.</span>
              </button>
              <button className={"cost-method" + (!f.flatRate ? " on" : "")} aria-pressed={!f.flatRate} onClick={() => set({ flatRate: false })}>
                <span className="cost-method-h"><Icon name="layers" size={14} />Detailed components</span>
                <span className="faint">Every part with its own cost, quantity and supplier. Slower to set up, but it drives ordering, purchase orders and inventory too.</span>
              </button>
            </div>
            {f.flatRate && (
              <div className="pe-row2" style={{ marginTop: 14, marginBottom: 0 }}>
                <Field label="Material cost" hint="$ per foot of fence, posts and concrete included">
                  <div className="pe-money-input"><span>$</span><input className="tnum" type="number" min="0" value={f.flatPerFt} onChange={e => set({ flatPerFt: +e.target.value || 0 })} /><span className="muted" style={{ fontWeight: 400, fontSize: 12.5 }}>/ft</span></div>
                </Field>
              </div>
            )}
          </Block>

          {/* components — the detailed method's own block */}
          {!f.flatRate && <Block icon="layers" title="Panel Components" right={
            <div className="row gap-8">
              <button className="btn btn-ghost btn-sm" onClick={resetTemplate}><Icon name="undo" size={13} />Reset to Template</button>
              <button className="btn btn-outline btn-sm" onClick={addRow}><Icon name="plus" size={13} />Add Component</button>
            </div>}>
            <p className="muted" style={{ fontSize: 12, margin: "0 0 12px" }}>Track every component cost individually. Post row auto-syncs from Post Configuration. Toggle <Icon name="arrow" size={11} style={{ transform: "rotate(90deg)", verticalAlign: "middle" }} /> for items that scale with panel length.</p>
            <div className="pe-table">
              <datalist id="pe-suppliers">{supNames.map((n: any) => <option key={n} value={n} />)}</datalist>
              <div className="pe-th"><span>Type</span><span>Description</span><span>SKU / Item #</span><span>Supplier</span><span style={{ textAlign: "center" }}>Scale</span><span style={{ textAlign: "center" }}>Qty</span><span style={{ textAlign: "right" }}>Unit $</span><span style={{ textAlign: "right" }}>Line $</span><span></span></div>
              {f.components.map((c, i) => {
                const scaledQty = c.scales && f.panelLength ? Math.round((+c.qty || 0) * f.panelLength / baseLen) : null;
                const nOpts = (c.options || []).length;
                return (
                  <React.Fragment key={i}>
                    <div className="pe-tr">
                      <select className="pe-cell-select" aria-label="Component type" value={c.type} onChange={e => updateRow(i, { type: e.target.value })}>{COMPONENT_TYPES.map(t => <option key={t}>{t}</option>)}</select>
                      {/* description is a COMBOBOX: type freely, the ▾ caret on
                          every row opens the nested options list (colors /
                          supplier alternates) right under the row */}
                      <div style={{ position: "relative", minWidth: 0 }}>
                        <input className="pe-cell-input" style={{ width: "100%", paddingRight: nOpts ? 44 : 30 }} value={c.desc} placeholder="Description" onChange={e => updateRow(i, { desc: e.target.value })} />
                        <button className={"pe-combo" + (nOpts ? " has" : "") + (optOpen === i ? " open" : "")}
                          title={nOpts ? nOpts + " saved option" + (nOpts > 1 ? "s" : "") + " — click to pick or edit" : "Options — add color / supplier alternates for this item"}
                          onClick={() => setOptOpen(optOpen === i ? null : i)}>
                          {nOpts > 0 && <span className="pe-combo-n">{nOpts}</span>}<Icon name="chevdown" size={12} style={{ transform: optOpen === i ? "rotate(180deg)" : "none" }} />
                        </button>
                      </div>
                      <input className="pe-cell-input" value={c.sku || ""} placeholder="SKU" title="Supplier item number — rides the BOM, POs & inventory matching" onChange={e => updateRow(i, { sku: e.target.value })} />
                      <input className="pe-cell-input" list="pe-suppliers" value={c.supplier || ""} placeholder="Supplier" title="Who you buy this from — inventory suppliers autocomplete here" onChange={e => updateRow(i, { supplier: e.target.value })} />
                      <button className={"scale-chk" + (c.scales ? " on" : "")} onClick={() => updateRow(i, { scales: !c.scales })} title="Scales with length">{c.scales ? <Icon name="arrow" size={12} style={{ transform: "rotate(90deg)" }} /> : ""}</button>
                      <div className="pe-cell" data-l="Qty" style={{ position: "relative" }}>
                        <input className="pe-cell-input tnum" type="number" aria-label="Quantity" value={c.qty} onChange={e => updateRow(i, { qty: +e.target.value || 0 })} style={{ textAlign: "center", width: "100%" }} />
                        {scaledQty != null && scaledQty !== (+c.qty || 0) && <span className="scale-hint">→{scaledQty}</span>}
                      </div>
                      {/* wrapped so the stacked mobile card can hang a "Unit $"
                          label off the cell — the header row is hidden there */}
                      <div className="pe-cell" data-l="Unit $">
                        <input className="pe-cell-input tnum" type="number" aria-label="Unit cost" value={c.unit} onChange={e => updateRow(i, { unit: +e.target.value || 0 })} style={{ textAlign: "right", width: "100%" }} />
                      </div>
                      <span className="tnum pe-line">{PE_money((+c.qty || 0) * (+c.unit || 0))}</span>
                      {c.locked ? <span style={{ width: 22 }} /> : <button className="pe-del" onClick={() => removeRow(i)}><Icon name="x" size={13} /></button>}
                    </div>
                    {optOpen === i && (
                      <div style={{ margin: "0 0 6px 18px", padding: "10px 12px", background: "var(--surface-2)", border: "1px solid var(--line)", borderLeft: "3px solid var(--teal)", borderRadius: 10 }}>
                        <div className="row between" style={{ marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
                          <span className="kicker" style={{ margin: 0, color: "var(--teal)" }}>└ Options for “{c.desc || c.type}” — Use swaps one in as the live row</span>
                          <button className="btn btn-outline btn-sm" onClick={() => addOption(i)}><Icon name="plus" size={12} />Add option — duplicates current</button>
                        </div>
                        {nOpts === 0
                          ? <p className="faint" style={{ fontSize: 11.5, margin: 0 }}>Ten cap choices across suppliers? Different panel colors? Duplicate the current values, tweak the name / SKU / price on each, and swap with <b>Use</b> when quoting a different setup.</p>
                          : (c.options || []).map((o, oi) => {
                              const live = (o.desc || "") === (c.desc || "") && (o.sku || "") === (c.sku || "") && (+o.unit || 0) === (+c.unit || 0);
                              return (
                                <div key={o.id || oi} className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap", marginBottom: 4 }}>
                                  <input className="pe-cell-input" style={{ flex: "2 1 180px" }} value={o.desc || ""} placeholder='Option — "Tan panel", "Gothic cap — Master Halco"' onChange={e => setOption(i, oi, { desc: e.target.value })} />
                                  <input className="pe-cell-input" style={{ width: 90 }} value={o.sku || ""} placeholder="SKU" onChange={e => setOption(i, oi, { sku: e.target.value })} />
                                  <input className="pe-cell-input" list="pe-suppliers" style={{ width: 110 }} value={o.supplier || ""} placeholder="Supplier" onChange={e => setOption(i, oi, { supplier: e.target.value })} />
                                  <input className="pe-cell-input tnum" type="number" style={{ width: 84, textAlign: "right" }} value={o.unit} onChange={e => setOption(i, oi, { unit: +e.target.value || 0 })} />
                                  <button className={"btn btn-sm " + (live ? "btn-primary" : "btn-outline")} title="Make this the live row — it prices the product" onClick={() => useOption(i, oi)}>{live ? "In use" : "Use"}</button>
                                  <button className="pe-del" onClick={() => delOption(i, oi)}><Icon name="x" size={13} /></button>
                                </div>
                              );
                            })}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            <div className="row between" style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--line)" }}>
              <span className="muted" style={{ fontSize: 13 }}>{f.components.length} components</span>
              <span className="row gap-8" style={{ alignItems: "baseline" }}><span className="muted" style={{ fontSize: 13 }}>Panel Total</span><span className="display tnum" style={{ fontSize: 20, color: "var(--teal)" }}>{PE_money(panelTotal)}</span></span>
            </div>
          </Block>}

          {/* summary */}
          <div className="pe-summary">
            <SummaryStat value={PE_money(panelTotal)} label="Per Panel" />
            <SummaryStat value={PE_money(perFoot)} label="Per Foot" accent="var(--teal)" />
            <SummaryStat value={PE_money(per100)} label="Per 100 ft" />
            <SummaryStat value={f.components.length} label="Components" />
          </div>

          {/* gates — one line per gate you offer; the ◉ default per size prices
              the quote, extra lines are your alternates (standard vs upgraded
              hardware, different suppliers) ready to swap in */}
          <Block icon="box" title="Gate Options" right={<span className="chip" style={{ fontSize: 10, color: "var(--teal)", borderColor: "var(--teal-tint, var(--line))", background: "var(--teal-tint, transparent)" }}>YOUR COST</span>}>
            <p className="muted" style={{ fontSize: 12.5, margin: "0 0 12px" }}>One line per gate you offer — <b style={{ color: "var(--fg-2)" }}>material cost, SKU, supplier</b>. The estimate's gate width picks the size (<b>≤6 ft</b> → Walk, <b>7–11</b> → 10 ft Drive, <b>12+</b> → 12 ft Drive) and the <b style={{ color: "var(--fg-2)" }}>◉ default</b> in that size prices it; keep alternates ("upgraded hardware", other suppliers) on their own lines and flip the default — or tweak the gate line on any single quote via Edit estimate.</p>
            {(f.gates.list || []).length === 0 && <p className="faint" style={{ fontSize: 12, margin: "0 0 8px" }}>No gates yet — add your walk gate first; duplicate it for hardware upgrades and drive sizes.</p>}
            <div style={{ display: "grid", gap: 6 }}>
              {(f.gates.list || []).map((g0: any, gi: number) => {
                const list = f.gates.list || [];
                const setList = (next: any) => set({ gates: { ...f.gates, list: next } });
                const upd = (patch: any) => setList(list.map((x: any, j: number) => j === gi ? { ...x, ...patch } : x));
                return (
                  <div key={g0.id || gi} className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
                    <button className={"sw" + (g0.default ? " on" : "")} title={g0.default ? "Default for this size — prices the quote" : "Make this the default for its size"}
                      style={{ flexShrink: 0 }} onClick={() => setList(list.map((x: any, j: number) => x.tier === g0.tier ? { ...x, default: j === gi } : x))}><span className="knob" /></button>
                    <input className="pe-cell-input" style={{ flex: "2 1 190px" }} value={g0.name || ""} placeholder='Gate — "Walk gate — standard hardware"' onChange={e => upd({ name: e.target.value })} />
                    <select className="pe-cell-select" style={{ width: 130, flexShrink: 0 }} value={g0.tier || "walk"} onChange={e => upd({ tier: e.target.value })}>
                      <option value="walk">Walk · ≤6 ft</option>
                      <option value="drive10">Drive · 10 ft</option>
                      <option value="drive12">Drive · 12 ft+</option>
                    </select>
                    <input className="pe-cell-input" style={{ width: 90, flexShrink: 0 }} value={g0.sku || ""} placeholder="SKU" onChange={e => upd({ sku: e.target.value })} />
                    <input className="pe-cell-input" list="pe-suppliers" style={{ width: 110, flexShrink: 0 }} value={g0.supplier || ""} placeholder="Supplier" onChange={e => upd({ supplier: e.target.value })} />
                    <span className="row" style={{ gap: 4, alignItems: "center", flexShrink: 0 }}>
                      <span className="muted" style={{ fontSize: 12 }}>$</span>
                      <input className="pe-cell-input tnum" type="number" min="0" style={{ width: 88, textAlign: "right" }} value={g0.cost == null ? "" : g0.cost} onChange={e => upd({ cost: +e.target.value || 0 })} />
                    </span>
                    <button className="pe-del" title="Duplicate this gate — then tweak the copy" onClick={() => setList([...list.slice(0, gi + 1), { ...g0, id: "g" + Date.now(), name: (g0.name || "Gate") + " — copy", default: false }, ...list.slice(gi + 1)])}><Icon name="plus" size={13} /></button>
                    <button className="pe-del" onClick={() => setList(list.filter((_: any, j: number) => j !== gi))}><Icon name="x" size={13} /></button>
                  </div>
                );
              })}
            </div>
            <div className="row gap-8" style={{ marginTop: 10 }}>
              <button className="btn btn-outline btn-sm" onClick={() => set({ gates: { ...f.gates, list: [...(f.gates.list || []), { id: "g" + Date.now(), name: "", tier: "walk", cost: 0, sku: "", supplier: "", default: !(f.gates.list || []).some((x: any) => x.tier === "walk" && x.default) } ] } })}><Icon name="plus" size={13} />Add gate</button>
              <span className="faint" style={{ fontSize: 11.5, alignSelf: "center" }}>the + on a row duplicates it — fastest way to build "standard / upgraded" pairs</span>
            </div>
          </Block>

          {/* install-time per-step overrides — sparse on this product's fence type */}
          <div className="card pad pe-block">
            <button className="row between" onClick={() => setStepTimeOpen(o => !o)} style={{ width: "100%", background: "none", border: 0, cursor: "pointer", padding: 0, flexWrap: "wrap", gap: 10 }}>
              <span className="row gap-8 kicker kicker-teal"><Icon name="clipboard" size={14} />Install time per step
                {overrideCount > 0 && <span className="chip teal" style={{ fontSize: 9.5, padding: "1px 7px" }}>{overrideCount} override{overrideCount === 1 ? "" : "s"}</span>}
              </span>
              <Icon name={stepTimeOpen ? "chevdown" : "chevright"} size={15} className="muted" />
            </button>
            {stepTimeOpen && (
              <div style={{ marginTop: 14 }}>
                <p className="muted" style={{ fontSize: 12.5, margin: "0 0 6px" }}>Overrides your <b style={{ color: "var(--fg-2)" }}>global step times</b> for this fence type ({f.category}) on the crew sheet's per-step &amp; job-time estimates. <b style={{ color: "var(--fg-2)" }}>Blank = use the global rate</b> (shown as the placeholder). Only the values you change are stored on the product.</p>
                {overrideCount > 0 &&
                  <div className="row" style={{ justifyContent: "flex-end", marginBottom: 6 }}>
                    <button className="btn btn-ghost btn-sm" onClick={resetStepRates}><Icon name="undo" size={13} />Reset all to global</button>
                  </div>}

                <div className="kicker" style={{ margin: "10px 0 10px" }}>Minutes per unit, by component</div>
                <div className="labor-grid">
                  {relevantStepKeys.filter(k => (STEP_RATE_KEYS as string[]).includes(k)).map((key) => {
                    const gv = globalRates[key];
                    const ov = f.stepRates[key];
                    const isSet = ov != null && ov !== "";
                    return (
                      <div key={key} className={"labor-cell" + (isSet ? " on" : "")}>
                        <div className="row between" style={{ gap: 6 }}>
                          <div className="labor-cat">{STEP_RATE_LABELS[key] || key}</div>
                          {isSet && <button className="pe-del" title="Use global rate" onClick={() => clearStepRate(key)}><Icon name="x" size={12} /></button>}
                        </div>
                        <div className="crew-num" style={{ margin: "6px 0 4px" }}>
                          <input className="tnum" type="number" min="0" step="0.1" value={isSet ? ov : ""} placeholder={String(gv)} onChange={e => setStepRate(key, e.target.value)} /><span className="muted">min</span>
                        </div>
                        <div className="faint" style={{ fontSize: 11 }}>{isSet ? `was ${gv} global` : `global ${gv} / ${key}`}</div>
                      </div>
                    );
                  })}
                </div>

                <div className="kicker" style={{ margin: "16px 0 10px" }}>Flat survey steps {hasPerFt ? <span className="faint" style={{ textTransform: "none", letterSpacing: 0 }}>— + optional per-100 ft</span> : null}</div>
                <div className="labor-grid">
                  {relevantStepKeys.filter(k => Object.keys(flatStepMinutes).includes(k) || perFtKeySet.has(k)).map((key) => {
                    const gv = globalRates[key];
                    const ov = f.stepRates[key];
                    const isSet = ov != null && ov !== "";
                    const isPerFt = perFtKeySet.has(key);
                    return (
                      <div key={key} className={"labor-cell" + (isSet ? " on" : "")}>
                        <div className="row between" style={{ gap: 6 }}>
                          <div className="labor-cat">{STEP_RATE_LABELS[key] || key}</div>
                          {isSet && <button className="pe-del" title="Use global rate" onClick={() => clearStepRate(key)}><Icon name="x" size={12} /></button>}
                        </div>
                        <div className="crew-num" style={{ margin: "6px 0 4px" }}>
                          <input className="tnum" type="number" min="0" step={isPerFt ? "0.5" : "1"} value={isSet ? ov : ""} placeholder={String(gv)} onChange={e => setStepRate(key, e.target.value)} /><span className="muted">min</span>
                        </div>
                        <div className="faint" style={{ fontSize: 11 }}>{isPerFt ? `per 100 ft · global ${gv}` : `flat · global ${gv} (≈ ${fmtDur(+gv || 0)})`}</div>
                      </div>
                    );
                  })}
                </div>
                <p className="faint" style={{ fontSize: 11, margin: "12px 0 0" }}>Per-100 ft adds time on bigger properties (e.g. set <b style={{ color: "var(--fg-3)" }}>Mark property — per 100 ft</b> to 5 → a 300 ft run adds ~15 min). Leave at 0 (the global default) for no size scaling. Saved with the product.</p>
              </div>
            )}
          </div>

          {/* U-channel post-trim add-on (vinyl only) */}
          {f.category === "Vinyl" &&
            <Block icon="ruler" title="U-channel Post Trim" right={<span className="chip" style={{ fontSize: 10, color: "var(--teal)", borderColor: "var(--teal-tint, var(--line))", background: "var(--teal-tint, transparent)" }}>YOUR COST</span>}>
              <p className="muted" style={{ fontSize: 12.5, margin: "0 0 14px" }}>Optional premium add-on for clean panel edges — one U-channel where each panel meets a post (<b style={{ color: "var(--fg-2)" }}>2 per panel</b>). Set what a channel costs you and the install time per channel; on a quote it shows as its own marked-up line. Off by default — enable it per bid on the estimate.</p>
              <div className="pe-row2">
                <Field label="U-channel" hint="$ per channel incl. screws">
                  <div className="pe-money-input"><span>$</span><input className="tnum" type="number" placeholder="4" value={f.uchannelCost} onChange={e => set({ uchannelCost: +e.target.value || 0 })} /></div>
                </Field>
                <Field label="U-channel" hint="min per channel">
                  <input className="input tnum" type="number" placeholder="1.5" value={f.uchannelLaborMin} onChange={e => set({ uchannelLaborMin: +e.target.value || 0 })} />
                </Field>
              </div>
            </Block>}

          {/* PROD-05: on a phone the only Save was at the very top of a very
              long form. This one rides with you and shows the live total. */}
          <div className="pe-sticky-save">
            {/* the live customer price from the shared engine — the component
                total means nothing once the per-foot method is selected */}
            <span className="pe-sticky-sum"><b className="tnum">{PE_money(stickyJob.perFoot)}</b><span className="muted">/ft installed · {f.flatRate ? "per-foot cost" : `${PE_money(panelTotal)} per panel`}</span></span>
            {edited && <span className="chip amber" style={{ fontSize: 10, padding: "1px 7px" }}>Unsaved</span>}
            <button className="btn btn-primary btn-sm" onClick={save}><Icon name="download" size={14} />Save product</button>
          </div>
          <div className="row between" style={{ marginTop: 6 }}>
            {editing && editing.custom ? <button className="btn btn-sm" style={{ background: "var(--red-tint)", color: "var(--red)", border: "1px solid var(--red-tint)" }} onClick={del}><Icon name="trash" size={14} />Delete</button> : <span />}
            <div className="row gap-8">
              <button className="btn btn-ghost" onClick={leave}>Cancel</button>
              <button className="btn btn-primary" onClick={save}><Icon name="download" size={15} />Save Product</button>
            </div>
          </div>
        </main>

        {/* right rail */}
        <aside className="pe-side">
          {/* PROD-06: the "Profitability Ranking · Top 5" panel here was a
              permanent placeholder — a decorative empty card on every load. */}
          <div className="card pad">
            <div className="row between" style={{ marginBottom: 10 }}><span className="kicker">What you sell</span>
              {missing.length > 0 && <span className="chip amber" style={{ fontSize: 10, padding: "1px 7px" }}>{missing.length} to set up</span>}</div>
            <p className="faint" style={{ fontSize: 11.5, margin: "0 0 9px" }}>
              {declared ? "Only these count towards being set up." : "Taken from what you already price. Tick the materials you actually sell and the rest stop counting."}
            </p>
            <div className="sold-grid">
              {FENCE_CATEGORIES.map(c => {
                const on = sold.includes(c);
                const has = store.allProducts.some((p: any) => p.category === c);
                return (
                  <button key={c} className={"sold-chip" + (on ? " on" : "") + (on && !has ? " todo" : "")} aria-pressed={on}
                    title={on ? (has ? "You price this" : "You sell this but have no product for it yet") : "Not offered — doesn't count towards setup"}
                    onClick={() => setSold(on ? sold.filter(x => x !== c) : [...sold, c])}>
                    {on && <Icon name={has ? "check" : "bolt"} size={11} />}{c}
                  </button>
                );
              })}
            </div>
            {missing.length > 0 && (
              <div style={{ marginTop: 10 }}>
                {missing.map(c => (
                  <div key={c} className="row between" style={{ padding: "7px 0", borderTop: "1px solid var(--line)", fontSize: 13 }}>
                    <span className="row gap-8"><Icon name="bolt" size={13} style={{ color: "var(--amber)" }} />{c}</span>
                    <button className="pe-add-link" onClick={() => addCategory(c)}>Add →</button>
                  </div>))}
              </div>
            )}
            {missing.length === 0 && <div className="chip green" style={{ width: "100%", justifyContent: "center", marginTop: 8 }}><Icon name="check" size={13} />Everything you sell is priced</div>}
          </div>
        </aside>
      </div>
      {toast && <div className="est-toast fade-up" style={{ position: "fixed", top: 84 }}>{toast}</div>}
    </div>
  );
}

function editorFromProduct(p) {
  const base = blankFor(p.category && CATS[p.category] ? p.category : "Wood");
  return Object.assign(base, {
    style: (CATS[p.category] && CATS[p.category].styles.includes((p.sub || "").split(" · ")[1])) ? (p.sub || "").split(" · ")[1] : base.style,
    customName: p.name || "", description: typeof p.description === "string" ? p.description : "", panelLength: p.panelWidth, postCost: p.postKit,
    components: [
      { type: "Post", desc: "Post kit", qty: 1, unit: p.postKit, scales: false, locked: true },
      { type: "Panel/Insert", desc: (p.name || "Panel") + " panel", qty: 1, unit: p.panelCost, scales: true },
      { type: "Concrete", desc: "Concrete bag", qty: p.bagsPerPost, unit: p.concretePerBag, scales: false },
    ],
    gates: { walk: String(p.gateCost || ""), drive10: "", drive12: "" },
    uchannelCost: 4, uchannelLaborMin: 1.5,
  });
}

/* keep only finite, non-inherited override keys from the editor's stepRates map —
   the sparse table that rides on the product (absent keys inherit global/default) */
function sparseStepRates(raw: any) {
  const out: any = {};
  if (raw && typeof raw === "object") {
    for (const k of Object.keys(raw)) { const v = raw[k]; if (v !== "" && v != null && isFinite(+v)) out[k] = +v; }
  }
  return out;
}

/* the step-rate keys worth surfacing for a fence category: the per-unit classes
   its build steps actually price from, plus the two flat survey steps and their
   optional per-100ft companions. Falls back to all keys if nothing resolves. */
function stepKeysForCategory(category: any) {
  let steps: any[] = [];
  try { steps = buildStepsFor({ category, gates: 1 }); } catch (e) { steps = []; }
  const classKeys: string[] = [];
  for (const k of STEP_RATE_KEYS) {
    if (steps.some((st: any) => Array.isArray(st.qk) && st.qk.includes(k))) classKeys.push(k as string);
  }
  // flat survey steps are on every build sequence ("Mark property", "Shoot grade"
  // when graded) — always offer them + their per-100ft size-scaling companions
  const flatKeys = Object.keys(flatStepMinutes);
  const perFtKeys = flatKeys.map((t) => PER_FT_STEP_OF[t]).filter(Boolean);
  const keys = [...classKeys, ...flatKeys, ...perFtKeys];
  // safety: if a category somehow priced from nothing, show the full set
  return keys.length > flatKeys.length + perFtKeys.length ? keys : [...STEP_RATE_KEYS as string[], ...flatKeys, ...perFtKeys];
}

/* small parts */
function Block({ icon, title, right, children }: any) {
  return (
    <div className="card pad pe-block">
      <div className="row between" style={{ marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <span className="row gap-8 kicker kicker-teal"><Icon name={icon} size={14} />{title}</span>{right}
      </div>
      {children}
    </div>
  );
}
function Field({ label, hint, children }: any) {
  return (
    <div className="pe-field">
      <label className="field-label">{label}{hint && <span className="faint" style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}> — {hint}</span>}</label>
      {children}
    </div>
  );
}
function GatePrice({ label, hint, value, onChange }: any) {
  return (
    <div className="pe-field">
      <label className="field-label">{label}{hint && <span className="faint" style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}> ({hint})</span>}</label>
      <div className="pe-money-input"><span>$</span><input className="tnum" type="number" placeholder="—" value={value} onChange={e => onChange(e.target.value)} /></div>
    </div>
  );
}
function SummaryStat({ value, label, accent }: any) {
  // font size lives in CSS (.pe-sum-val) so the narrowest phones can shrink it —
  // "$2,975.00" at 24px overflowed a half-width cell at 320px
  return <div className="pe-sum-cell"><div className="display tnum pe-sum-val" style={{ color: accent || "var(--fg)" }}>{value}</div><div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{label}</div></div>;
}
function MiniIO({ label, value, placeholder, accent }: any) {
  return (
    <div className="mc-row">
      <label>{label}</label>
      <div className="mc-input"><input className="tnum" readOnly value={value} placeholder={placeholder} style={accent && value ? { color: accent } : null} /></div>
    </div>
  );
}

Object.assign(window, { ProductEditor });

export { blankFor, ProductEditor, editorFromProduct, Block, Field, GatePrice, SummaryStat, MiniIO, PE_money, COMPONENT_TYPES, CAT_COLOR, CATS, FENCE_CATEGORIES };
